import { useRef, useCallback } from 'react';
import { Flow, FlowConfig } from './Flow';

/**
 * Returns a ref to a Flow instance that persists across renders.
 * On flow completion, the ref is reset to a new Flow instance.
 */
export function useFlow<ExtraMetadata extends object = object>(
  name: string,
  initialMetadata?: Partial<ExtraMetadata>,
  flowConfig?: FlowConfig
) {
  // store to ref so it persists across renders without causing re-renders
  const flowRef = useRef<Flow<ExtraMetadata>>(
    Flow.create(name, initialMetadata, {
      ...flowConfig,
      onComplete() {
        flowConfig?.onComplete?.(flowRef.current);
        // reset the flow ref to a new flow on completion
        flowRef.current = Flow.create(name, initialMetadata, flowConfig);
      },
    })
  );

  const resetFlow = useCallback(() => {
    flowRef.current = Flow.create(name, initialMetadata, flowConfig);
    // ignore eslint because initialMetadata and flowConfig are objects and would cause updates every render and probably won't need to be changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  return {
    flowRef, // return the ref so calling code can access current. resetFlow won't work properly if we just return flowRef.current
    resetFlow,
  };
}
